// src/components/SubmitScoreDialog.js
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Typography,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { submitCompetitionResult } from '../api/competitionApi';

export default function SubmitScoreDialog({ open, onClose, competition, registration, userId, onSuccess }) {
    const [formData, setFormData] = useState({
        workoutId: '',
        result: '',
        videoUrl: '',
        timeCap: false,     // ✅ LISA SEE
        reps: ''            // ✅ LISA SEE
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const selectedWorkout = competition?.workouts?.find(
        w => w.id === parseInt(formData.workoutId)
    );

    const validateResultFormat = (result, resultType) => {
        if (!result) return false;

        if (resultType === 'TIME') {
            return /^\d{2}:\d{2}:\d{2}$/.test(result);
        } else if (resultType === 'REPS') {
            return /^\d+$/.test(result);
        } else if (resultType === 'WEIGHT') {
            return /^\d+(\.\d+)?$/.test(result);
        }
        return false;
    };

    const getFormatHint = (resultType) => {
        if (resultType === 'TIME') {
            return 'Format: hh:mm:ss (e.g., 00:12:34)';
        } else if (resultType === 'REPS') {
            return 'Enter whole number (e.g., 150)';
        } else if (resultType === 'WEIGHT') {
            return 'Enter weight in kg (e.g., 225.5)';
        }
        return '';
    };

    const getPlaceholder = (resultType) => {
        if (resultType === 'TIME') {
            return '00:12:34';
        } else if (resultType === 'REPS') {
            return '150';
        } else if (resultType === 'WEIGHT') {
            return '225.5';
        }
        return '';
    };

    const handleSubmit = async () => {
        setError('');

        if (!formData.workoutId) {
            setError('Please select a workout');
            return;
        }

        // ✅ MUUDA SEE: Validate based on TC
        if (selectedWorkout.resultType === 'TIME' && formData.timeCap) {
            // TC validation
            if (!formData.reps || isNaN(formData.reps) || parseInt(formData.reps) < 0) {
                setError('Please enter valid reps number for TC result');
                return;
            }
        } else {
            // Normal result validation
            if (!formData.result) {
                setError('Please enter a result');
                return;
            }

            if (!validateResultFormat(formData.result, selectedWorkout.resultType)) {
                setError(getFormatHint(selectedWorkout.resultType));
                return;
            }
        }

        try {
            setLoading(true);
            await submitCompetitionResult(competition.id, formData.workoutId, {
                registrationId: registration.id,
                result: formData.timeCap ? `TC-${formData.reps}` : formData.result,  // ✅ Format for TC
                videoUrl: formData.videoUrl || null,
                submittedBy: userId,
                timeCap: formData.timeCap,                                          // ✅ LISA SEE
                reps: formData.timeCap ? parseInt(formData.reps) : null            // ✅ LISA SEE
            });
            setFormData({ workoutId: '', result: '', videoUrl: '', timeCap: false, reps: '' });
            onSuccess();
        } catch (error) {
            console.error('Error submitting score:', error);
            setError(error.response?.data?.error || 'Failed to submit score');
        } finally {
            setLoading(false);
        }
    };

    const handleWorkoutChange = (workoutId) => {
        setFormData(prev => ({
            ...prev,
            workoutId,
            result: '',
            timeCap: false,
            reps: ''
        }));
    };

    const handleTimeCapChange = (checked) => {
        setFormData(prev => ({
            ...prev,
            timeCap: checked,
            result: '',
            reps: ''
        }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Submit Workout Score
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {competition?.isTeamCompetition && registration?.teamName && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Submitting score for team: <strong>{registration.teamName}</strong>
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormControl fullWidth required>
                            <InputLabel>Select Workout</InputLabel>
                            <Select
                                value={formData.workoutId}
                                onChange={(e) => handleWorkoutChange(e.target.value)}
                                label="Select Workout"
                            >
                                {competition?.workouts?.map((workout, index) => (
                                    <MenuItem key={workout.id} value={workout.id}>
                                        Workout #{index + 1}: {workout.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {selectedWorkout && (
                        <>
                            <Grid item xs={12}>
                                <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Workout Description
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedWorkout.workoutDescriptions[registration.category] || 'No description available'}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* ✅ LISA SEE: TC Checkbox kui TIME workout */}
                            {selectedWorkout.resultType === 'TIME' && (
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.timeCap}
                                                onChange={(e) => handleTimeCapChange(e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body1">
                                                    TC (Time Cap)
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Check this if you didn't complete the workout within the time cap
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Grid>
                            )}

                            {/* ✅ MUUDA SEE: Conditional input based on TC */}
                            <Grid item xs={12}>
                                {formData.timeCap ? (
                                    <TextField
                                        fullWidth
                                        required
                                        type="number"
                                        label="Reps Completed"
                                        value={formData.reps}
                                        onChange={(e) => setFormData(prev => ({ ...prev, reps: e.target.value }))}
                                        placeholder="150"
                                        helperText="Enter the number of repetitions you completed"
                                        inputProps={{ min: 0 }}
                                    />
                                ) : (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Your Result"
                                        value={formData.result}
                                        onChange={(e) => setFormData(prev => ({ ...prev, result: e.target.value }))}
                                        placeholder={getPlaceholder(selectedWorkout.resultType)}
                                        helperText={getFormatHint(selectedWorkout.resultType)}
                                    />
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="YouTube Video URL (Optional)"
                                    value={formData.videoUrl}
                                    onChange={(e) => setFormData(prev => ({ ...prev, videoUrl: e.target.value }))}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    helperText="Add a video of your workout performance"
                                />
                            </Grid>
                        </>
                    )}

                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error">{error}</Alert>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={
                        loading ||
                        !formData.workoutId ||
                        (!formData.timeCap && !formData.result) ||
                        (formData.timeCap && !formData.reps)
                    }
                >
                    {loading ? 'Submitting...' : 'Submit Score'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}